import { useEffect, useRef, useState } from 'react';

import { Button } from '../../components/Button';
import { Divider } from '../../components/Divider';
import { Dropdown } from '../../components/Dropdown';
import { PageHeading } from '../../components/PageHeading';
import { SectionHeader } from '../../components/SectionHeader';
import { SideImage } from '../../components/SideImage';
import { PreferencesPanel } from '../../composites/PreferencesPanel';
import { ReservationPanel } from '../../composites/ReservationPanel';
import { SplitLayout } from '../../composites/SplitLayout';
import { ChevronBackIcon } from '../../icons/ChevronBackIcon';
import { CloseIcon } from '../../icons/CloseIcon';
import { InfoIcon } from '../../icons/InfoIcon';
import {
  BackButton,
  BottomBar,
  BottomBarTitle,
  CardSection,
  ChangeRestaurantButton,
  Content,
  DepositBanner,
  EmptyTimeslots,
  FieldLabel,
  DepositDismiss,
  DepositText,
  FormPanel,
  MenuCard,
  MenuField,
  PanelStack,
  Progress,
  ProgressDot,
  ProgressSegment,
  TimeslotGrid,
  RestaurantList,
  RestaurantName,
  RestaurantOption,
  RestaurantOptionText,
  RestaurantSubtitle,
  RestaurantThumb,
  RestaurantThumbPlaceholder,
  SelectedRestaurant,
  SelectedRestaurantGroup,
  TopBar,
  TopBarSlot,
} from './DiningReservationTemplate.styles';
import type {
  DiningReservationRestaurant,
  DiningReservationTemplateProps,
} from './DiningReservationTemplate.types';

const RESTAURANT_LIST_ID = 'dining-restaurant-list';

const noop = () => undefined;

function RestaurantCardContent({ restaurant }: { restaurant: DiningReservationRestaurant }) {
  return (
    <>
      <RestaurantOptionText>
        <RestaurantName>{restaurant.name}</RestaurantName>
        {restaurant.subtitle && <RestaurantSubtitle>{restaurant.subtitle}</RestaurantSubtitle>}
      </RestaurantOptionText>
      {restaurant.imageUrl ? (
        <RestaurantThumb src={restaurant.imageUrl} alt={restaurant.name} />
      ) : (
        <RestaurantThumbPlaceholder aria-hidden />
      )}
    </>
  );
}

export function DiningReservationTemplate({
  imageUrl,
  imageAlt,
  onBack,
  progressStep = 1,
  progressTotal = 2,
  restaurants,
  selectedRestaurantId,
  onSelectRestaurant,
  guestCount,
  minGuests,
  maxGuests,
  guestStep,
  onGuestCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  timeslots,
  isLoading,
  selectedTimeslot,
  onTimeslotSelect,
  menuOptions,
  selectedMenuId,
  onMenuSelect,
  depositMessage,
  preferenceCategories,
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  onReserve,
  reserveDisabled,
  labels,
}: DiningReservationTemplateProps) {
  const selectedRestaurant = restaurants.find((r) => r.id === selectedRestaurantId) ?? null;
  const restaurantSelected = selectedRestaurant !== null;
  const [pickerExpanded, setPickerExpanded] = useState(false);
  const [depositDismissed, setDepositDismissed] = useState(false);
  const [restaurantPickTick, setRestaurantPickTick] = useState(0);
  const restaurantSectionRef = useRef<HTMLDivElement>(null);
  const pickerShown = !selectedRestaurant || pickerExpanded;

  useEffect(() => {
    if (restaurantPickTick === 0) return;
    restaurantSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [restaurantPickTick]);

  const handleSelectRestaurant = (id: string) => {
    onSelectRestaurant(id);
    setPickerExpanded(false);
    setRestaurantPickTick((tick) => tick + 1);
  };
  const dateSelected = restaurantSelected && Boolean(selectedDate);
  const hasTimeslots = Boolean(timeslots?.length);
  const showMenu = restaurantSelected && Boolean(menuOptions?.length);
  const selectedMenuLabel = menuOptions?.find((option) => option.value === selectedMenuId)?.label;
  const menuFieldLabel = labels.menuFieldLabel ?? 'Menu';
  const showPreferences = restaurantSelected && Boolean(preferenceCategories?.length);
  const canReserve = restaurantSelected && Boolean(selectedDate) && Boolean(selectedTimeslot);

  return (
    <SplitLayout
      leftWidth="45%"
      height="100vh"
      flushRightContent
      containedScroll
      left={<SideImage src={imageUrl} alt={imageAlt} sticky />}
      right={
        <>
          <TopBarSlot>
            <TopBar>
              {onBack && (
                <BackButton type="button" onClick={onBack} aria-label={labels.back ?? 'Back'}>
                  <ChevronBackIcon />
                </BackButton>
              )}
              <Progress>
                {Array.from({ length: progressTotal }).map((_, index) =>
                  index === 0 ? (
                    <ProgressSegment key={index} $active={index < progressStep} />
                  ) : (
                    <ProgressDot key={index} $active={index < progressStep} />
                  )
                )}
              </Progress>
            </TopBar>
          </TopBarSlot>

          <Content>
            <PageHeading title={labels.heading} subtitle={labels.subheading} />

            <PanelStack>
              <FormPanel>
                <CardSection ref={restaurantSectionRef} $wide>
                  <SectionHeader
                    title={labels.restaurantTitle}
                    subtitle={pickerShown ? labels.restaurantSubtitle : undefined}
                  />
                  {pickerShown ? (
                    <RestaurantList id={RESTAURANT_LIST_ID}>
                      {restaurants.map((restaurant) => (
                        <RestaurantOption
                          key={restaurant.id}
                          type="button"
                          $selected={restaurant.id === selectedRestaurantId}
                          aria-pressed={restaurant.id === selectedRestaurantId}
                          onClick={() => handleSelectRestaurant(restaurant.id)}
                        >
                          <RestaurantCardContent restaurant={restaurant} />
                        </RestaurantOption>
                      ))}
                    </RestaurantList>
                  ) : (
                    selectedRestaurant && (
                      <SelectedRestaurantGroup>
                        <SelectedRestaurant>
                          <RestaurantCardContent restaurant={selectedRestaurant} />
                        </SelectedRestaurant>
                        {restaurants.length > 1 && (
                          <ChangeRestaurantButton
                            type="button"
                            aria-expanded={false}
                            aria-controls={RESTAURANT_LIST_ID}
                            onClick={() => setPickerExpanded(true)}
                          >
                            {labels.changeRestaurant ?? 'Change restaurant'}
                          </ChangeRestaurantButton>
                        )}
                      </SelectedRestaurantGroup>
                    )
                  )}
                </CardSection>

                {restaurantSelected && (
                  <>
                    <Divider />
                    <CardSection>
                      <ReservationPanel
                        title={labels.reservationTitle}
                        subtitle={labels.reservationSubtitle}
                        guestLabel={labels.guestLabel}
                        guestCount={guestCount}
                        minGuests={minGuests}
                        maxGuests={maxGuests}
                        guestStep={guestStep}
                        onGuestCountChange={onGuestCountChange}
                        activeDate={activeDate}
                        selectedDate={selectedDate}
                        calendarView={calendarView}
                        availability={availability}
                        onDateSelect={onDateSelect}
                        onWeekNavigate={onWeekNavigate}
                        onCalendarViewChange={onCalendarViewChange}
                        calendarLabels={labels.calendar}
                        counterDecreaseLabel={labels.counter?.decrease}
                        counterIncreaseLabel={labels.counter?.increase}
                      />

                      {dateSelected && (
                        <>
                          <Divider />
                          <FieldLabel>{labels.timeLabel}</FieldLabel>
                          {hasTimeslots && timeslots ? (
                            <TimeslotGrid>
                              {timeslots.map((slot) => (
                                <Button
                                  key={slot.value}
                                  variant="toggle"
                                  size="small"
                                  fullWidth
                                  selected={slot.value === selectedTimeslot}
                                  onClick={() => onTimeslotSelect(slot.value)}
                                  type="button"
                                >
                                  {slot.label}
                                </Button>
                              ))}
                            </TimeslotGrid>
                          ) : isLoading ? null : (
                            <EmptyTimeslots>{labels.noTimeslotsLabel}</EmptyTimeslots>
                          )}
                        </>
                      )}

                      {(showMenu || depositMessage) && (
                        <MenuCard>
                          {showMenu && menuOptions && (
                            <>
                              <FieldLabel>{labels.menuTitle}</FieldLabel>
                              <MenuField>
                                <Dropdown
                                  options={menuOptions}
                                  value={selectedMenuId}
                                  onChange={onMenuSelect ?? noop}
                                  placeholder={`${menuFieldLabel}*`}
                                  aria-label={
                                    selectedMenuLabel
                                      ? `${labels.menuTitle}: ${selectedMenuLabel}`
                                      : labels.menuTitle
                                  }
                                  align="left"
                                />
                              </MenuField>
                            </>
                          )}
                          {depositMessage && !depositDismissed && (
                            <DepositBanner>
                              <InfoIcon />
                              <DepositText>{depositMessage}</DepositText>
                              <DepositDismiss
                                type="button"
                                aria-label={labels.dismiss ?? 'Dismiss'}
                                onClick={() => setDepositDismissed(true)}
                              >
                                <CloseIcon />
                              </DepositDismiss>
                            </DepositBanner>
                          )}
                        </MenuCard>
                      )}
                    </CardSection>
                  </>
                )}
              </FormPanel>

              {showPreferences && preferenceCategories && (
                <FormPanel>
                  <CardSection $compact>
                    <PreferencesPanel
                      categories={preferenceCategories}
                      onOptionToggle={onPreferenceOptionToggle ?? noop}
                      onOtherChange={onPreferenceOtherChange ?? noop}
                      onFreeTextChange={onPreferenceFreeTextChange ?? noop}
                      otherButtonLabel={labels.preferences?.otherButtonLabel}
                    />
                  </CardSection>
                </FormPanel>
              )}
            </PanelStack>
          </Content>

          <BottomBar>
            <BottomBarTitle>{selectedRestaurant?.name ?? ''}</BottomBarTitle>
            <Button
              type="button"
              variant="primary"
              size="large"
              onClick={onReserve}
              disabled={(reserveDisabled ?? false) || !canReserve}
            >
              {labels.reserveLabel}
            </Button>
          </BottomBar>
        </>
      }
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
